import { type FormEvent, useState } from 'react';
import { captchaHeaders, Turnstile } from '../components/Turnstile';
import { Alert, AuthLayout, Button, Field, TextLink } from '../components/ui';
import { authClient, authErrorMessage, siteUrl } from '../lib/auth-client';
import { usePublicConfig } from '../lib/public-config';

export function ForgotPassword() {
  const config = usePublicConfig();
  const [email, setEmail] = useState('');
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [captchaReset, setCaptchaReset] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const { error: failed } = await authClient.requestPasswordReset({
      email: email.trim(),
      redirectTo: siteUrl('/reset-password'),
      fetchOptions: { headers: captchaHeaders(captcha) },
    });
    setBusy(false);
    if (failed) {
      setError(authErrorMessage(failed));
      setCaptchaReset((n) => n + 1);
      return;
    }
    setSent(true);
  }

  if (sent) {
    return (
      <AuthLayout
        title="Check your email"
        footer={<TextLink to="/sign-in">Back to sign in</TextLink>}
      >
        <p className="text-ink">
          If <strong className="text-ink">{email.trim()}</strong> has a MOGZO account, we’ve sent it
          a link to choose a new password. The link works for 1 hour.
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="Enter your account’s email and we’ll send you a link."
      footer={<TextLink to="/sign-in">Back to sign in</TextLink>}
    >
      <form onSubmit={onSubmit} className="space-y-4">
        {error && <Alert>{error}</Alert>}
        <Field
          label="Email"
          name="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <Turnstile
          siteKey={config?.turnstileSiteKey ?? null}
          onToken={setCaptcha}
          resetKey={captchaReset}
        />
        <Button busy={busy} disabled={!!config?.turnstileSiteKey && !captcha}>
          Send reset link
        </Button>
      </form>
    </AuthLayout>
  );
}
